const POPUP_WIDTH = 320
const MAX_POPUP_HEIGHT = 400
const ESTIMATED_POPUP_HEIGHT = 280
const GAP = 8
const VIEWPORT_MARGIN = 8

export interface ViewportSize {
  width: number
  height: number
}

export interface PopupAnchorRect {
  top: number
  bottom: number
  left: number
}

export interface PopupPlacement {
  left: number
  width: number
  maxHeight: number
  top?: number
  bottom?: number
  placement: 'above' | 'below'
}

export interface PopupPlacementOptions {
  popupHeight?: number
  viewport?: ViewportSize
}

export function computePopupPlacement(
  anchorRect: PopupAnchorRect,
  options?: PopupPlacementOptions,
): PopupPlacement {
  const vw = options?.viewport?.width ?? window.innerWidth
  const vh = options?.viewport?.height ?? window.innerHeight
  const popupHeight = options?.popupHeight ?? ESTIMATED_POPUP_HEIGHT

  const width = Math.min(POPUP_WIDTH, vw - VIEWPORT_MARGIN * 2)
  let left = anchorRect.left
  if (left + width > vw - VIEWPORT_MARGIN) {
    left = vw - VIEWPORT_MARGIN - width
  }
  left = Math.max(VIEWPORT_MARGIN, left)

  const spaceBelow = vh - anchorRect.bottom - VIEWPORT_MARGIN
  const spaceAbove = anchorRect.top - VIEWPORT_MARGIN
  const neededHeight = Math.min(popupHeight, MAX_POPUP_HEIGHT) + GAP

  const fitsBelow = spaceBelow >= neededHeight
  const fitsAbove = spaceAbove >= neededHeight

  let placement: 'above' | 'below'
  if (fitsBelow && !fitsAbove) {
    placement = 'below'
  } else if (fitsAbove && !fitsBelow) {
    placement = 'above'
  } else {
    placement = spaceBelow >= spaceAbove ? 'below' : 'above'
  }

  if (placement === 'below') {
    return {
      left,
      width,
      top: anchorRect.bottom + GAP,
      maxHeight: Math.min(MAX_POPUP_HEIGHT, Math.max(0, spaceBelow - GAP)),
      placement: 'below',
    }
  }

  return {
    left,
    width,
    bottom: vh - anchorRect.top + GAP,
    maxHeight: Math.min(MAX_POPUP_HEIGHT, Math.max(0, spaceAbove - GAP)),
    placement: 'above',
  }
}
